import React, { useEffect } from 'react';
import ReactFlow, {
  Handle,
  MarkerType,
  Position,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './KitIpDiagram.css';

// Read-only map of the rented kit and the customer layer. React Flow places
// the nodes and edges. Visitors do not pan or zoom; the camera fits once.

const W = 220;
const H = 124;
const col = (i) => i * 276;
const row = (i) => i * 176;

function CardNode({ data }) {
  return (
    <div className={`kit-ip-card kit-ip-card-${data.tone}`}>
      <Handle type="target" position={Position.Left} id="l" />
      <Handle type="target" position={Position.Top} id="t" />
      <Handle type="source" position={Position.Right} id="r" />
      <Handle type="source" position={Position.Bottom} id="b" />
      <span className="kit-ip-kicker">{data.kicker}</span>
      <strong>{data.title}</strong>
      <p>{data.body}</p>
    </div>
  );
}

const nodeTypes = { card: CardNode };

function card(id, x, y, tone, kicker, title, body) {
  return {
    id,
    type: 'card',
    position: { x, y },
    width: W,
    height: H,
    style: { width: W, height: H },
    data: { tone, kicker, title, body },
    draggable: false,
    selectable: false,
    connectable: false,
  };
}

const nodes = [
  card('kit', col(0), row(0), 'rent', 'You rent', 'App Kit', 'The foundation. You never edit it. A starter copy lands in your repo once.'),
  card('agent', col(1), row(0), 'path', 'Reads the kit', 'Dev Console', 'Edits your repo only. A commit cannot contain kit code.'),
  card('layer', col(2), row(0), 'yours', 'You own', 'Your layer', 'Your pages, workers, and data. Only the extension seams.'),
  card('export', col(3), row(0), 'yours', 'If you leave', 'Export', 'A git bundle of that layer, when you ask. The kit is not in it.'),
  card('gate', col(2), row(1), 'gate', 'Each deploy', 'Build gate', 'Must stay inside those seams, or the build stops. Then the pinned kit and your layer become one app.'),
  card('run', col(3), row(1), 'path', 'Goes live', 'Running app', 'Our host, or your cloud. License every 12 hours. Usage counts every 6. Content stays put.'),
];

const story = nodes.map((n) => n.data);

function edge(id, source, target, opts = {}) {
  const color = opts.color || '#9aa0a6';
  return {
    id,
    source,
    target,
    sourceHandle: opts.sourceHandle || 'r',
    targetHandle: opts.targetHandle || 'l',
    type: 'straight',
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
    style: {
      stroke: color,
      strokeWidth: 1.5,
      ...(opts.dashed ? { strokeDasharray: '6 4' } : {}),
    },
  };
}

const surf = '#14e0c8';
const yours = '#4ade80';
const gate = '#f87171';

const edges = [
  edge('e-kit-agent', 'kit', 'agent', { color: surf, dashed: true }),
  edge('e-agent-layer', 'agent', 'layer', { color: yours }),
  edge('e-layer-export', 'layer', 'export', { color: yours }),
  edge('e-layer-gate', 'layer', 'gate', { color: yours, sourceHandle: 'b', targetHandle: 't' }),
  edge('e-gate-run', 'gate', 'run', { color: gate }),
];

function FitFrame() {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const pane = document.querySelector('.kit-ip-canvas');
    if (!pane) return undefined;
    const fit = () => fitView({ padding: 0.08, duration: 0 });
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(pane);
    return () => ro.disconnect();
  }, [fitView]);
  return null;
}

export default function KitIpDiagram() {
  return (
    <div className="kit-ip">
      <div className="kit-ip-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.08 }}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          nodesFocusable={false}
          edgesFocusable={false}
          proOptions={{ hideAttribution: true }}
          style={{ background: 'transparent' }}
        >
          <FitFrame />
        </ReactFlow>
      </div>
      <ol className="kit-ip-story">
        {story.map((item) => (
          <li key={item.title} className={`kit-ip-card kit-ip-card-${item.tone}`}>
            <span className="kit-ip-kicker">{item.kicker}</span>
            <strong>{item.title}</strong>
            <p>{item.body}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
